// eslint:disable max-func-body-length
import { maxLengthOfEmailsInputValue } from 'app/area/company-report/constants';
import { Metadata, ScheduleDefinition, ScheduleFailedConstraints } from 'app/area/company-report/types-store';
import { useInstanceKey } from 'app/area/favourite/favourite-context';
import { FiltersCompanyReport } from 'app/area/filters/filters-company-report';
import { selectCurrentList, selectReportData } from 'app/area/filters/selectors';
import { CreateReport } from 'app/area/saved-reports/create-report';
import { SpinLoader } from 'app/components';
import { FavouriteDefinition, SharedState } from 'app/types-shared';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { ButtonCallToAction, MeatballMenu, Textarea, ValidationLevel, ValidationProps } from 'ui';
import { MainGridContainer } from 'ui/common/main-grid-container/main-grid-container';
import { ScheduleReport } from 'app/area/company-report/components/schedule-report';
import styles from './report-screen.scss';
import { useEmailSubscribersToCompanyReportDefinitionEffect} from 'app/area/company-report/hooks';
import { isValidListOfEmails } from 'app/infrastructure/validation/email';
import { Tag } from 'app/components/tag';
import { Icon } from 'ui/common/icon';
import { toAbsoluteUrl } from 'app/infrastructure/hosting';
import { routeConstants } from 'app/routes/route-constants';
import { Link } from 'react-router-dom';
import { Handlers, HotKeys, KeyMap } from 'ui/common/hotkeys';

interface ReportScreenProps {
  companyReportId: number;
  sharedState: SharedState;
  translation: any;
  onChangeEmail: (value: string, validation?: ValidationProps) => void;
  keyMap: KeyMap;
  handlers: Handlers;
  sendReport: () => void;
  saveReport: () => void;
  emails: string;
  metadata: Metadata;
  fetchingReport: boolean;
  fetchingMetadata: boolean;
  emailsValidation?: ValidationProps;
  validateFilters: boolean;

  isCreateReportOpen: boolean;
  onCloseReport: () => void;
  onCreateReport: () => void;
  onUpdateReport: () => void;
  searchRequiredUsers: boolean;
  updateExistingReport: boolean;
  definition: FavouriteDefinition;
  deleteScheduleDefinition: () => void;
  showUpdate?: boolean;
  reportIsDisabled?: boolean;
  scheduleDefinition: ScheduleDefinition;
  updateScheduleDefinition: (scheduleDefinition: ScheduleDefinition, validateSchedule?: boolean) => void;
  fetchingScheduleDefinition: boolean;
  scheduleDefinitionChanged: boolean;
  createScheduleDefinition: () => void;
  scheduleFailedConstraints: ScheduleFailedConstraints;
  updateScheduleDefinitionOnSubmit: () => void;
}

export function ReportScreen({
  metadata, translation, sharedState, onChangeEmail, fetchingReport, fetchingScheduleDefinition, keyMap, handlers, 
  emails, sendReport, saveReport, companyReportId, validateFilters, fetchingMetadata, isCreateReportOpen, searchRequiredUsers, 
  updateExistingReport, onCloseReport, definition, onCreateReport, onUpdateReport, showUpdate, reportIsDisabled, deleteScheduleDefinition, 
  scheduleDefinition, updateScheduleDefinition, createScheduleDefinition, scheduleFailedConstraints, updateScheduleDefinitionOnSubmit
}: ReportScreenProps) {

  useEmailSubscribersToCompanyReportDefinitionEffect(emails, metadata?.eMailReport);
  const [invalidEmail, setInvalidEmail] = React.useState(false);
  const instanceKey = useInstanceKey();
  const reportData = useSelector(selectReportData(instanceKey));
  const currentListFilters = useSelector(selectCurrentList(instanceKey));

  const errorParamsRequired = {level: ValidationLevel.error,
    messages: [{level: ValidationLevel.error, message: `${translation.invalid_email_error}`}]
  };

  const emailValidation = !!emails && invalidEmail && !isValidListOfEmails(emails) && errorParamsRequired;
  const iconAbsolutePath = toAbsoluteUrl('i/recurrence.svg');
  const iconSchedule = <Icon url={iconAbsolutePath} />;

  const onBlurEmail = ()=> {
    if(!isValidListOfEmails(emails)) {
      setInvalidEmail(true);
      return false;
    }
    setInvalidEmail(false);
    return true;
  };
  
   const sendReportSection = <>
    <div>
      <h3 className="tertiary-group-title">
       <span>{translation.generate_title}</span>
      </h3>
    </div>
    <HotKeys keyMap={keyMap} handlers={handlers}>
      <div>
        <div className="row">
          <div className="col-12 col-md-7">
            <Textarea
              componentId="0"
              className="email-input"
              onChange={onChangeEmail}
              readOnly={fetchingReport}
              validation={emailValidation}
              value={emails}
              onBlur={onBlurEmail}
              maxLength={maxLengthOfEmailsInputValue}
              placeholder={translation.enter_email_recipients}
            />
          </div>
        </div>
      </div>
    </HotKeys>
  </>;

  const sendOrGenerateReport = metadata?.eMailReport && emails ? sendReport : saveReport;  
  const companyReportdAdmin = sharedState.access?.adminPanel?.companyReport?.administrationCentral ?? false;
  
  const sendOrGenerateReportButton = 
    !isCreateReportOpen && <div className={styles.generateButton}>
      <ButtonCallToAction onClick={sendOrGenerateReport} disabled={!currentListFilters.length}>
        {translation.generate_button}
      </ButtonCallToAction>
    </div>;

  const meatballMenu = <div className="meatball-menu float-end position-relative">
    <MeatballMenu closeAfterClick={true}>
      <li>
        <button type="button" onClick={onCreateReport} className="dropdown-item" disabled={reportIsDisabled}>
          {translation.save_new_company_report}
        </button>
      </li>
      {showUpdate && <li>
        <button type="button" onClick={onUpdateReport} className="dropdown-item" disabled={reportIsDisabled}>
          {translation.update_existing_company_report}
        </button>
      </li>}
      {companyReportdAdmin && <>
        <li>
          <Link className="dropdown-item" to={routeConstants.companyReportAdminItemFor(companyReportId)}>
            {translation.edit_company_report_config}
          </Link>
        </li>         
        <li>
          <Link className="dropdown-item" to={routeConstants.companyReportAdminList}>
            {translation.manage_company_reports_option}
          </Link>
        </li>
      </>}
    </MeatballMenu>
  </div>;

  return (
    <MainGridContainer className={styles.reportPanel}>
      {meatballMenu}
      {!metadata || fetchingMetadata ? null : sendOrGenerateReportButton}
      <h1 className={styles.header}>{reportData?.reportTypeDescription}
        {reportIsDisabled && <span className={styles.disabledTag}>
          <Tag>{translation.disabled}</Tag>
        </span>}
      </h1>
      <h3>{definition?.favourite?.d || translation.report_title}</h3>
      {isCreateReportOpen && <CreateReport
        closeReportCreator={onCloseReport}
        definition={definition}
        searchRequiredUsers={searchRequiredUsers}
        updateReport={updateExistingReport}
        companyReportId={companyReportId}
        className={styles.createReportSection}
        update_report_title={translation.update_existing_company_report}
        title={translation.save_new_company_report}
        reportIsDisabled={reportIsDisabled}
        scheduleFailedConstraints={scheduleFailedConstraints}
        updateScheduleDefinitionOnSubmit={updateScheduleDefinitionOnSubmit}
        hasScheduleDefinition={!!scheduleDefinition}
      >
        <ScheduleReport
          definition={definition}
          onUpdateReport={onUpdateReport}
          showUpdate={showUpdate}
          deleteScheduleDefinition={deleteScheduleDefinition}
          translation={translation}
          scheduleDefinition={scheduleDefinition}
          updateScheduleDefinition={updateScheduleDefinition}
          fetchingScheduleDefinition={fetchingScheduleDefinition}
          locale={sharedState?.locale}
          reportIsDisabled={reportIsDisabled}
          createScheduleDefinition={createScheduleDefinition}
          scheduleFailedConstraints={scheduleFailedConstraints}
        />
      </CreateReport>}
      <FiltersCompanyReport
        companyReportId={companyReportId}
        definition={definition}
        subtitle={translation.report_subtitle}
        predefinedTitle={translation.filters_predefined_date}
        validateFilters={validateFilters}
        currentList={currentListFilters}
        locale={sharedState?.locale}
      />
      {!isCreateReportOpen && 
      <div>
        <h3 className="tertiary-group-title">
          <span>{translation.schedule_title}</span>
        </h3>
        <span className={styles.iconSchedule}>{iconSchedule}</span>
        <span>
          {!!scheduleDefinition ? translation.scheduled_for_auto_execute : translation.not_scheduled_for_auto_execute}
        </span>
      </div>}
      {metadata?.eMailReport && sendReportSection}
      {(fetchingReport || fetchingMetadata) && <SpinLoader />}
    </MainGridContainer>
  );
}
